import React from "react";
import { Clock } from "lucide-react";
import { InlineUtmCell, buildUtmLink } from "./UtmBuilder";
import { BulkInjectButton } from "./BulkInject";

const formatNumber = (num) => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return String(num ?? 0);
};

const formatCurrency = (n) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Per-video metrics table with inline UTM/short-link cell and per-row inject
 * button. Also surfaces a "Inject all" toolbar above the table when the user
 * has YouTube write access.
 */
export function VideoTable({ videos, utmSettings, utmOverrides = {}, onOverrideChange, ytStatus, onReauthorize }) {
  if (!videos.length) {
    return <p className="text-zinc-500 text-sm">No videos yet — connect a channel to see your top performers.</p>;
  }
  return (
    <div className="space-y-3">
      {/* Bulk toolbar — only shows the "Inject all" affordance for now */}
      <div className="flex items-center justify-end px-6 sm:px-8">
        <BulkInjectButton
          videos={videos.map((vm) => vm)}
          ytStatus={ytStatus}
          onReauthorize={onReauthorize}
          utmSettings={utmSettings}
        />
      </div>
      <div className="overflow-x-auto -mx-6 sm:-mx-8" data-testid="video-table">
        <table className="w-full min-w-[1080px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold border-b border-white/10">
              <th className="text-left px-6 sm:px-8 py-3">Video</th>
              <th className="text-right px-3 py-3">Views</th>
              <th className="text-right px-3 py-3">Clicks</th>
              <th className="text-right px-3 py-3">CTR</th>
              <th className="text-right px-3 py-3">Sales</th>
              <th className="text-right px-3 py-3">Revenue</th>
              <th className="text-right px-3 py-3">EPC</th>
              <th className="text-left px-6 sm:px-8 py-3">UTM link · short · override</th>
            </tr>
          </thead>
          <tbody>
            {videos.map((vm) => {
              const v = vm.video;
              const override = utmOverrides[v.video_id] ?? null;
              const utmLink = buildUtmLink(utmSettings, v.video_id, v.title, override);
              return (
                <tr key={v.video_id} className="border-b border-white/[0.05] hover:bg-white/[0.03] transition-colors">
                  <td className="px-6 sm:px-8 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <img src={v.thumbnail_url} alt={v.title} className="w-20 h-12 rounded-md object-cover border border-white/10 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-white truncate max-w-[260px]">{v.title}</div>
                        <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                          <Clock className="w-3 h-3" /> {v.duration}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="text-right text-sm text-zinc-300 px-3">{formatNumber(v.view_count)}</td>
                  <td className="text-right text-sm text-zinc-300 px-3">{formatNumber(v.clicks)}</td>
                  <td className="text-right text-sm text-red-400 px-3">{vm.click_through_rate}%</td>
                  <td className="text-right text-sm text-zinc-300 px-3">{formatNumber(v.sales_count)}</td>
                  <td className="text-right text-sm text-white font-medium px-3">{formatCurrency(v.sales_value)}</td>
                  <td className="text-right text-sm text-zinc-400 px-3">${vm.earnings_per_click}</td>
                  <td className="px-6 sm:px-8 py-3 min-w-[360px] max-w-[560px]">
                    <InlineUtmCell
                      link={utmLink}
                      testidPrefix={`utm-cell-${v.video_id}`}
                      videoId={v.video_id}
                      videoTitle={v.title}
                      utmSettings={utmSettings}
                      override={override}
                      onOverrideChange={onOverrideChange}
                      ytStatus={ytStatus}
                      onReauthorize={onReauthorize}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
